import React, { useState, useEffect, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import '../../styles/calendrier.css';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_API_URL;
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// Crée une date locale sans heure
const toLocalDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Ajoute un jour pour corriger le décalage UTC
const addOneDay = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
};

const PageCalendrier = () => {
  const [dateSelectionnee, setDateSelectionnee] = useState(new Date());
  const [evenements, setEvenements] = useState([]);
  const [evenementModal, setEvenementModal] = useState(null);

  useEffect(() => {
    const chargerEvenements = async () => {
      try {
        const res = await axiosInstance.get('/api/evenements');
        setEvenements(res.data);
      } catch (err) {
        console.error("Erreur lors du chargement des événements :", err);
      }
    };
    chargerEvenements();
  }, []);

  const getEventsForDate = (date) => {
    const d = toLocalDate(date);
    return evenements.filter(evt => {
      const evtDate = addOneDay(toLocalDate(evt.date)); // ajoute 1 jour pour compenser
      return evtDate.getTime() === d.getTime();
    });
  };

  const getEventsForMonth = (date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const today = toLocalDate(new Date());

    return evenements.filter(evt => {
      const evtDate = addOneDay(toLocalDate(evt.date)); // ajoute 1 jour pour compenser
      return (
        evtDate.getMonth() === month &&
        evtDate.getFullYear() === year &&
        evtDate >= today
      );
    });
  };

  const buildEventSummaries = (events) => {
    const groups = {};
    events.forEach((evt) => {
      const parts = evt.titre.split(" ");
      const prefix = parts.slice(0, -1).join(" ");
      const suffix = parts.slice(-1)[0];
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(suffix);
    });
    return Object.entries(groups).map(([prefix, suffixes]) => {
      if (suffixes.length === 1) return `${prefix} ${suffixes[0]}`;
      return `${prefix} ${suffixes.join(", ")}`;
    });
  };


  const eventsForCurrentMonth = useMemo(() => getEventsForMonth(dateSelectionnee), [dateSelectionnee, evenements]);


  const handleTileClick = (date) => {
    const dayEvents = getEventsForDate(date);
    setEvenementModal({ date, events: dayEvents });
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Calendrier des activités</h1>

      <div className="mb-6">
        <Calendar
          locale="fr-FR"
          onChange={setDateSelectionnee}
          onActiveStartDateChange={({ activeStartDate }) => {
            setDateSelectionnee(activeStartDate);
          }}
          value={dateSelectionnee}
          tileClassName={({ date, view }) => {
            if (view !== 'month') return null;
            return getEventsForDate(date).length > 0 ? 'jour-avec-evenement' : null;
          }}
          tileContent={({ date, view }) => {
            const dayEvents = getEventsForDate(date);
            return view === 'month' && dayEvents.length > 0 ? (
              <div className="events-container">
                {buildEventSummaries(dayEvents).map((titre, i) => (
                  <div
                    key={i}
                    className="text-xs text-green-700 cursor-pointer event-point"
                    onClick={() => handleTileClick(date)}
                  >
                    • {titre}
                  </div>
                ))}
              </div>
            ) : null;
          }}
          onClickDay={handleTileClick}
          className="custom-calendar"
        />
      </div>

      {/* Bloc des événements à venir pour le mois */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-center">
          Événements à venir en {dateSelectionnee.toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}
        </h2>

        {eventsForCurrentMonth.length === 0 ? (
          <p className="text-center">Aucun événement prévu ce mois-ci.</p>
        ) : (
          <ul className="space-y-4">
            {eventsForCurrentMonth
              .sort((a, b) => addOneDay(toLocalDate(a.date)) - addOneDay(toLocalDate(b.date)))
              .map((evt) => (
                <li key={evt._id} className="bg-white border p-4 rounded shadow">
                  <p className="text-sm text-gray-600 font-medium mb-1">
                    {addOneDay(toLocalDate(evt.date))?.toLocaleDateString('fr-FR')}
                  </p>
                  <h3 className="font-bold text-lg">{evt.titre}</h3>
                  <p>{evt.description}</p>
                  {evt.lien && (
                    <a
                      href={evt.lien}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Voir l'événement
                    </a>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>

      {evenementModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setEvenementModal(null)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <h2 className="text-xl font-bold mb-4">
              {evenementModal.events.length > 0
                ? `Événements le ${addOneDay(new Date(evenementModal.date)).toLocaleDateString()}`
                : `Aucun événement à cette date`}
            </h2>

            {evenementModal.events.length > 0 ? (
              evenementModal.events.map((evt, index) => (
                <div key={index} className="mb-4 border-b pb-4">
                  <h3 className="text-lg font-semibold">{evt.titre}</h3>
                  <p className="text-sm text-gray-600 mt-1">{evt.description}</p>
                  {evt.lien && (
                    <a
                      href={evt.lien}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-blue-600 underline"
                    >
                      Voir l'événement
                    </a>
                  )}
                </div>
              ))
            ) : (
              <p>Aucun événement prévu pour cette date.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageCalendrier;
